import React, {FC, ReactNode, useEffect, useRef, useState} from "react";
import {
  CheckBadgeIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  WifiIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {getEnvVariables} from "helpers/getEnvVariables";
import {BuyOrderType} from "data/types";
import {
  useGetWompiAcceptanceQuery,
  useLazyGetWompiTransactionQuery,
  usePayWithWompiCardMutation,
  useSaveOrderMutation,
  useTokenizeWompiCardMutation,
} from "store/api";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import NcModal from "shared/NcModal/NcModal";
import {SuccessModal} from "components/SuccessModal/SuccessModal";
import visaPng from "images/vis.png";
import amexPng from "images/amex.png";
import {ReactComponent as MastercardIcon} from "images/mastercard.svg";
import {
  CARD_RULES,
  WompiCustomer,
  WompiTransaction,
  browserInfo,
  cardBrandOf,
  formatCardNumber,
  formatExpiry,
  isExpiryValid,
  isLuhnValid,
  isSettled,
  parseTransaction,
  reasonOf,
  statusOf,
} from "./wompiHelpers";

const {REACT_APP_WOMPI_PUBLIC_KEY} = getEnvVariables();
const isSandbox = `${REACT_APP_WOMPI_PUBLIC_KEY ?? ""}`.startsWith("pub_test_");

const FIRST_CHECK_DELAY = 1500;
const CHECK_INTERVAL = 2000;
const CHECK_TIMEOUT = 5 * 60 * 1000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type Step =
  | "form"
  | "processing"
  | "challenge"
  | "pending"
  | "declined"
  | "failed"
  // Wompi approved it but the order couldn't be posted: the webhook places it.
  | "confirming";

export interface WompiProps {
  amount: number;
  /** Its devReference is already registered as a pending payment. */
  buyOrder: BuyOrderType;
  /** Registers a new pending payment for a retry (Wompi refuses a repeated reference). */
  newReference: () => Promise<string | null>;
  customer: WompiCustomer;
}

/**
 * Paying by card through Wompi, without its widget: tokenize the card, charge
 * it through the server, then follow the transaction (showing the bank's
 * page when 3D Secure asks for it) until it settles. Once approved, the
 * order is posted like with Nuvei.
 */
export const Wompi: FC<WompiProps> = ({
  amount,
  buyOrder,
  newReference,
  customer,
}) => {
  const {
    data: acceptanceData,
    error: acceptanceError,
    refetch: refetchAcceptance,
  } = useGetWompiAcceptanceQuery({});
  const [tokenizeCard] = useTokenizeWompiCardMutation();
  const [payWithCard] = usePayWithWompiCardMutation();
  const [getTransaction] = useLazyGetWompiTransactionQuery();
  const [saveOrder] = useSaveOrderMutation();

  const [number, setNumber] = useState("");
  const [holder, setHolder] = useState((customer.fullName ?? "").toUpperCase());
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");
  const [numberTouched, setNumberTouched] = useState(false);
  const [cvcTouched, setCvcTouched] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptData, setAcceptData] = useState(false);

  const [step, setStep] = useState<Step>("form");
  const [transaction, setTransaction] = useState<WompiTransaction | null>(null);
  const [isPaying, setIsPaying] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [alertMessage, setAlert] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Bumped to drop whatever is still running: a new payment, a retry, leaving.
  const run = useRef(0);
  const attempts = useRef(0);
  const reference = useRef(buyOrder.order.devReference);

  useEffect(
    () => () => {
      run.current++;
    },
    [],
  );

  const acceptance = acceptanceData?.data;
  const acceptanceReady =
    !!acceptance?.termsToken && !!acceptance?.personalDataToken;

  useEffect(() => {
    if (acceptanceError || (acceptanceData && !acceptanceReady)) {
      setUnavailable(statusOf(acceptanceError) >= 500);
      setStep("failed");
    }
  }, [acceptanceData, acceptanceError]);

  // ── Card rules ──

  const digits = number.replace(/\D/g, "");
  const brand = cardBrandOf(digits);
  const numberLength = brand ? CARD_RULES[brand].length : 16;
  const cvcLength = brand ? CARD_RULES[brand].cvcLength : 3;

  const numberOk =
    !!brand && digits.length === numberLength && isLuhnValid(digits);
  const holderOk = holder.trim().length >= 3;
  const expiryOk = isExpiryValid(expiry);
  const cvcOk = cvc.length === cvcLength;

  const ready =
    amount > 0 &&
    acceptanceReady &&
    numberOk &&
    holderOk &&
    expiryOk &&
    cvcOk &&
    acceptTerms &&
    acceptData;

  // Errors only once a field looks finished, never while typing it.
  const numberError = !brand
    ? digits.length >= 4
      ? "Solo aceptamos Visa, Mastercard y American Express"
      : null
    : digits.length === numberLength
      ? numberOk
        ? null
        : "Revisa el número de la tarjeta"
      : numberTouched && digits.length > 0
        ? `El número debe tener ${numberLength} dígitos`
        : null;
  const expiryError =
    expiry.length === 5 && !expiryOk ? "Fecha no válida" : null;
  const cvcError =
    cvcTouched && cvc.length > 0 && !cvcOk ? "CVC no válido" : null;

  // ── Actions ──

  const showAlert = (message: string) => {
    setAlert(message);
    setTimeout(() => setAlert(null), 3000);
  };

  const pay = async () => {
    if (!ready || isPaying) return;
    const current = ++run.current;
    setIsPaying(true);

    // Only the first attempt uses the reference registered before opening.
    attempts.current += 1;
    if (attempts.current > 1) {
      const fresh = await newReference();
      if (current !== run.current) return;
      if (!fresh) {
        setIsPaying(false);
        showAlert("Ocurrió un error, intenta de nuevo");
        return;
      }
      reference.current = fresh;
    }

    const [expMonth, expYear] = expiry.split("/");
    const token = await tokenizeCard({
      number: digits,
      cvc,
      exp_month: expMonth,
      exp_year: expYear,
      card_holder: holder.trim(),
    });
    if (current !== run.current) return;
    const cardToken = "data" in token ? token.data?.data?.id : undefined;
    if (!cardToken) {
      setIsPaying(false);
      showAlert("Wompi no aceptó esta tarjeta. Revisa los datos.");
      return;
    }

    const payment = await payWithCard({
      reference: reference.current,
      amountInCents: Math.round(amount * 100),
      acceptance: {
        termsToken: acceptance.termsToken,
        personalDataToken: acceptance.personalDataToken,
      },
      cardToken,
      installments: 1,
      browserInfo: browserInfo(),
      customer,
    });
    if (current !== run.current) return;
    if ("error" in payment) {
      const status = statusOf(payment.error);
      setIsPaying(false);
      if (status === 422) {
        // Wompi refused a detail (the card is spent: paying again tokenizes it anew).
        showAlert(
          reasonOf((payment.error as any).data) ||
            "Wompi no aceptó esta tarjeta. Revisa los datos.",
        );
        return;
      }
      setUnavailable(status >= 500);
      setStep("failed");
      return;
    }

    const started = parseTransaction(payment.data?.data);
    setTransaction(started);
    setStep("processing");
    setIsPaying(false);
    await follow(started.id, current);
  };

  /**
   * Asks for the transaction until it settles: the bank's page shows when it
   * asks the customer to confirm, and after CHECK_TIMEOUT it stays pending
   * (the webhook settles it).
   */
  const follow = async (id: string, current: number) => {
    const giveUpAt = Date.now() + CHECK_TIMEOUT;
    await sleep(FIRST_CHECK_DELAY);
    while (current === run.current) {
      const {data} = await getTransaction(id);
      if (current !== run.current) return;
      if (data?.data) {
        const latest = parseTransaction(data.data);
        setTransaction(latest);
        if (isSettled(latest.status)) {
          if (latest.status === "APPROVED") {
            await placeOrder(latest);
          } else {
            setStep("declined");
          }
          return;
        }
        setStep(latest.challenge ? "challenge" : "processing");
      }
      if (Date.now() > giveUpAt) {
        setStep("pending");
        return;
      }
      await sleep(CHECK_INTERVAL);
    }
  };

  const placeOrder = async (approved: WompiTransaction) => {
    setStep("processing");
    const {data: orderResponse, error: saveOrderError} = await saveOrder({
      ...buyOrder,
      order: {
        ...buyOrder.order,
        devReference: approved.reference,
        transactionId: approved.id,
      },
    });
    if (
      !saveOrderError &&
      orderResponse &&
      (orderResponse.meta.status === 201 || orderResponse.meta.status === 200)
    ) {
      setShowSuccessModal(true);
      return;
    }
    // Never ask to pay again: the charge went through.
    setStep("confirming");
  };

  const retry = () => {
    run.current++;
    setStep("form");
    setIsPaying(false);
    setUnavailable(false);
    setTransaction(null);
    setCvc("");
    setCvcTouched(false);
    if (!acceptanceReady) refetchAcceptance();
  };

  const fillTestCard = () => {
    setNumber("4242 4242 4242 4242");
    setExpiry(`12/${(new Date().getFullYear() + 3) % 100}`);
    setCvc("123");
    if (!holder.trim()) setHolder("BEE TEST");
  };

  const goToProfile = () => {
    window.location.href = "/profile";
  };

  // ── Views ──

  const transactionLabel = transaction?.id
    ? `Transacción ${transaction.id}`
    : undefined;

  const renderCover = () => {
    switch (step) {
      case "processing":
        return (
          <div
            role="status"
            className="flex flex-col items-center gap-4 py-16 text-center"
          >
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <div className="space-y-1">
              <h3 className="text-lg font-semibold">Confirmando tu pago</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                No cierres esta ventana, puede tardar unos segundos.
              </p>
            </div>
          </div>
        );
      case "challenge":
        return (
          <div className="space-y-4">
            <div className="space-y-1 text-center">
              <h3 className="text-lg font-semibold">
                Confirma el pago con tu banco
              </h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Tu banco necesita verificar que eres tú. Sigue los pasos de
                abajo.
              </p>
            </div>
            {/* A data URL keeps the bank's page off this site's origin. */}
            <iframe
              key={transaction?.challenge ?? ""}
              title="Verificación del banco"
              src={`data:text/html;charset=utf-8,${encodeURIComponent(
                transaction?.challenge ?? "",
              )}`}
              className="w-full h-[520px] rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white"
            />
          </div>
        );
      case "pending":
        return (
          <Outcome
            tone="amber"
            icon={<ClockIcon className="w-7 h-7" />}
            title="Pago en proceso"
            body="Wompi todavía está procesando tu pago. Te avisaremos cuando se confirme."
            reference={transactionLabel}
            primary={["Ir a mi perfil", goToProfile]}
          />
        );
      case "confirming":
        return (
          <Outcome
            tone="green"
            icon={<CheckBadgeIcon className="w-7 h-7" />}
            title="Pago aprobado"
            body="Estamos registrando tu compra. La verás en tu perfil en unos minutos."
            reference={transactionLabel}
            primary={["Ir a mi perfil", goToProfile]}
          />
        );
      case "declined":
        return (
          <Outcome
            tone="red"
            icon={<XMarkIcon className="w-7 h-7" />}
            title="Pago no aprobado"
            // Wompi's own reason when it gives one ("Fondos insuficientes"...).
            body={
              transaction?.statusMessage ||
              "No se pudo completar el pago. Puedes intentarlo de nuevo o elegir otro método."
            }
            reference={transactionLabel}
            primary={["Intentar de nuevo", retry]}
          />
        );
      case "failed":
        return unavailable ? (
          <Outcome
            tone="red"
            icon={<ExclamationTriangleIcon className="w-7 h-7" />}
            title="Pago no disponible"
            body="En este momento no podemos procesar el pago. Elige otro método o inténtalo más tarde."
          />
        ) : (
          <Outcome
            tone="red"
            icon={<WifiIcon className="w-7 h-7" />}
            title="No pudimos abrir el pago"
            body="Revisa tu conexión e inténtalo de nuevo."
            primary={["Intentar de nuevo", retry]}
          />
        );
      default:
        return null;
    }
  };

  const brandMark =
    brand === "visa" ? (
      <img className="w-8 h-5 object-contain" src={visaPng} alt="Visa" />
    ) : brand === "mastercard" ? (
      <MastercardIcon className="w-8 h-5" aria-label="Mastercard" />
    ) : brand === "amex" ? (
      <img className="w-8 h-5 object-contain" src={amexPng} alt="Amex" />
    ) : null;

  const renderForm = () => (
    <form
      className="space-y-5"
      autoComplete="on"
      onSubmit={(e) => {
        e.preventDefault();
        pay();
      }}
    >
      <div className="flex items-baseline justify-between rounded-2xl bg-neutral-100 dark:bg-neutral-900 px-4 py-3">
        <span className="text-sm text-neutral-500 dark:text-neutral-400">
          Total a pagar
        </span>
        <span className="text-xl font-semibold tabular-nums">
          ${amount.toFixed(2)}
        </span>
      </div>

      {isSandbox && (
        <button
          type="button"
          onClick={fillTestCard}
          className="w-full rounded-full border border-dashed border-amber-400 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
        >
          Modo de prueba · toca para usar una tarjeta de prueba
        </button>
      )}

      <Field label="Número de tarjeta" error={numberError}>
        <div className="relative">
          <Input
            value={number}
            onChange={(e) => setNumber(formatCardNumber(e.target.value))}
            onFocus={() => setNumberTouched(false)}
            onBlur={() => setNumberTouched(true)}
            inputMode="numeric"
            autoComplete="cc-number"
            placeholder="0000 0000 0000 0000"
            className="pr-20 tracking-wider tabular-nums"
            aria-invalid={!!numberError}
          />
          <div className="absolute inset-y-0 right-3 flex items-center gap-1.5">
            {brandMark}
            {numberOk && <CheckBadgeIcon className="w-5 h-5 text-green-600" />}
          </div>
        </div>
      </Field>

      <Field label="Nombre en la tarjeta">
        <Input
          value={holder}
          onChange={(e) => setHolder(e.target.value.toUpperCase())}
          autoComplete="cc-name"
          placeholder="COMO APARECE EN LA TARJETA"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Vence (MM/AA)" error={expiryError}>
          <Input
            value={expiry}
            onChange={(e) => setExpiry(formatExpiry(e.target.value))}
            inputMode="numeric"
            autoComplete="cc-exp"
            placeholder="MM/AA"
            className="tabular-nums"
            aria-invalid={!!expiryError}
          />
        </Field>
        <Field label="CVC" error={cvcError}>
          <Input
            type="password"
            value={cvc}
            onChange={(e) =>
              setCvc(e.target.value.replace(/\D/g, "").slice(0, cvcLength))
            }
            onFocus={() => setCvcTouched(false)}
            onBlur={() => setCvcTouched(true)}
            inputMode="numeric"
            autoComplete="cc-csc"
            placeholder={"•".repeat(cvcLength)}
            className="tabular-nums"
            aria-invalid={!!cvcError}
          />
        </Field>
      </div>

      <div className="space-y-3">
        <Acceptance
          checked={acceptTerms}
          onChange={setAcceptTerms}
          label="Acepto los términos y condiciones de Wompi"
          url={acceptance?.termsUrl}
        />
        <Acceptance
          checked={acceptData}
          onChange={setAcceptData}
          label="Autorizo el tratamiento de mis datos personales"
          url={acceptance?.personalDataUrl}
        />
      </div>

      <ButtonPrimary
        type="submit"
        className="w-full"
        disabled={!ready}
        loading={isPaying}
      >
        Pagar ${amount.toFixed(2)}
      </ButtonPrimary>

      <p className="flex items-center justify-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400">
        <LockClosedIcon className="w-4 h-4 text-green-600" />
        Pago protegido por Wompi
      </p>
    </form>
  );

  return (
    <div className="mx-auto w-full max-w-md py-2">
      {step === "form" ? renderForm() : renderCover()}
      {alertMessage && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-2xl shadow-2xl transition-all duration-300 ease-in-out opacity-90 hover:opacity-100 hover:scale-105 flex items-center gap-4">
          {alertMessage}
        </div>
      )}
      <NcModal
        isOpenProp={showSuccessModal}
        modalTitle="Compra exitosa"
        contentExtraClass="max-w-md"
        triggerText=""
        renderContent={() => <SuccessModal />}
      />
    </div>
  );
};

const Field: FC<{
  label: string;
  error?: string | null;
  children: ReactNode;
}> = ({label, error, children}) => (
  <label className="block space-y-1.5">
    <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
      {label}
    </span>
    {children}
    {error && (
      <span className="block text-xs text-red-600 dark:text-red-400">
        {error}
      </span>
    )}
  </label>
);

const Acceptance: FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  url?: string;
}> = ({checked, onChange, label, url}) => (
  <div className="flex items-start gap-3 text-sm">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={label}
      className="mt-0.5 h-5 w-5 shrink-0 rounded border-neutral-300 text-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-800"
    />
    <span className="flex-1 text-neutral-700 dark:text-neutral-300">
      {label}
    </span>
    {url && (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 font-semibold underline"
      >
        Leer
      </a>
    )}
  </div>
);

const TONES = {
  green: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  amber: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  red: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const Outcome: FC<{
  tone: keyof typeof TONES;
  icon: ReactNode;
  title: string;
  body: string;
  reference?: string;
  primary?: [string, () => void];
}> = ({tone, icon, title, body, reference, primary}) => (
  <div className="flex flex-col items-center gap-4 py-10 text-center">
    <div
      className={`flex h-14 w-14 items-center justify-center rounded-full ${TONES[tone]}`}
    >
      {icon}
    </div>
    <div className="space-y-1.5">
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm text-neutral-500 dark:text-neutral-400">{body}</p>
      {reference && (
        <p className="text-xs text-neutral-400 tabular-nums">{reference}</p>
      )}
    </div>
    {primary && (
      <ButtonPrimary className="mt-2 w-full" onClick={primary[1]}>
        {primary[0]}
      </ButtonPrimary>
    )}
  </div>
);
